import React, { useEffect, useState } from "react";
import axios from "axios";
import { Heart, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Heart button that toggles a property in the current user's favourites.
 * - Anonymous click → redirects to /login.
 * - Optimistically flips local state; reverts on server error.
 * - `initial` is the server-side flag (defaults to false).
 * - `onChange(next)` notifies the parent if it also tracks state.
 */
export default function FavouriteButton({
  propertyId,
  initial = false,
  variant = "chip", // "chip" for cards, "solid" for detail hero
  onChange,
  className = "",
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [fav, setFav] = useState(initial);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setFav(initial); }, [initial]);

  const toggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast("Sign in to save favourites");
      navigate("/login");
      return;
    }
    const next = !fav;
    setFav(next); setBusy(true);
    try {
      if (next) {
        await axios.post(`${API}/favourites/${propertyId}`);
      } else {
        await axios.delete(`${API}/favourites/${propertyId}`);
      }
      onChange?.(next);
      if (next) toast.success("Added to favourites");
    } catch (err) {
      setFav(!next); // revert
      toast.error(err.response?.data?.detail || "Could not update favourites");
    } finally { setBusy(false); }
  };

  const base = variant === "solid"
    ? "flex items-center gap-1 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold hover:border-copper"
    : "flex h-9 w-9 items-center justify-center rounded-full bg-white/90 backdrop-blur hover:bg-white transition";

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      aria-pressed={fav}
      title={fav ? "Remove from favourites" : "Save to favourites"}
      data-testid={`favourite-btn-${propertyId}`}
      className={`${base} ${className}`}
    >
      {busy ? (
        <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
      ) : (
        <Heart
          className={`h-5 w-5 transition ${fav ? "fill-copper text-copper" : "text-slate-500"}`}
          strokeWidth={fav ? 1.5 : 2}
        />
      )}
      {variant === "solid" && <span>{fav ? "Saved" : "Save"}</span>}
    </button>
  );
}
